import { useState, useCallback } from 'react'
import { SelectedFilters } from '@/models/filters'

export interface UseFiltersReturn {
  selectedFilters: SelectedFilters
  isDrawerOpen: boolean
  handleFilterChange: (
    groupName: string,
    optionKey: string,
    optionLabel: string,
    isSelected: boolean,
  ) => void
  clearAllFilters: () => void
  openDrawer: () => void
  closeDrawer: () => void
  getActiveFilterCount: () => number
}

export const useFilters = (
  onFiltersChange?: (filters: SelectedFilters) => void,
): UseFiltersReturn => {
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>({})
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  const handleFilterChange = useCallback(
    (
      groupName: string,
      optionKey: string,
      optionLabel: string,
      isSelected: boolean,
    ): void => {
      setSelectedFilters((prev) => {
        const newFilters = { ...prev }

        if (!newFilters[groupName]) {
          newFilters[groupName] = {}
        }

        if (isSelected) {
          newFilters[groupName] = {
            ...newFilters[groupName],
            [optionKey]: optionLabel,
          }
        } else {
          const { [optionKey]: _removed, ...rest } = newFilters[groupName]
          newFilters[groupName] = rest

          // Clean up empty groups
          if (Object.keys(newFilters[groupName]).length === 0) {
            const { [groupName]: _removedGroup, ...restGroups } = newFilters
            return restGroups
          }
        }

        // Notify parent component
        if (onFiltersChange) {
          setTimeout(() => onFiltersChange(newFilters), 0)
        }

        return newFilters
      })
    },
    [onFiltersChange],
  )

  const clearAllFilters = useCallback((): void => {
    setSelectedFilters({})
    if (onFiltersChange) {
      setTimeout(() => onFiltersChange({}), 0)
    }
  }, [onFiltersChange])

  const openDrawer = useCallback(() => setIsDrawerOpen(true), [])
  const closeDrawer = useCallback(() => setIsDrawerOpen(false), [])

  const getActiveFilterCount = useCallback((): number => {
    return Object.values(selectedFilters).reduce(
      (total, group) => total + Object.keys(group).length,
      0,
    )
  }, [selectedFilters])

  return {
    selectedFilters,
    isDrawerOpen,
    handleFilterChange,
    clearAllFilters,
    openDrawer,
    closeDrawer,
    getActiveFilterCount,
  }
}
